// Voice Prompt section for Profile setup / edit (Feb 2026).
//
// Records a 5-30s voice clip, uploads to POST /api/voice-prompt, and shows the
// resulting bubble with play/re-record affordances. Reuses VoiceRecorder for
// capture UI; renders VoiceBubble for playback of the saved prompt.
//
// Contract:
//   • Backend: POST /api/voice-prompt (multipart file + ?duration=&waveform=)
//   • Response: { prompt_id, url, duration, waveform, content_type }
//   • Profile field: profile.voice_prompt = {prompt_id, url, duration, waveform, ...}
//
// Server enforces 3s min, 30s max, transcribes via Whisper, safety-scans the
// transcript before persisting. Client mirrors the 3s min to prevent obvious
// short-tap dead-clicks that would 400.

import { useEffect, useState } from 'react';
import { api } from '@/contexts/AuthContext';
import { Mic, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import VoiceRecorder from '@/components/VoiceRecorder';
import VoiceBubble from '@/components/VoiceBubble';

export default function VoicePromptSection({ voicePrompt, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [prompt, setPrompt] = useState(voicePrompt || null);
  useEffect(() => setPrompt(voicePrompt || null), [voicePrompt]);

  const handleSend = async ({ blob, duration, peaks }) => {
    // Server rejects <3s server-side; catch it here so we don't waste an upload.
    if (duration < 3) {
      toast.error('Recording must be at least 3 seconds.');
      return;
    }
    setSaving(true);
    try {
      const form = new FormData();
      const ext = (blob.type.split('/')[1] || 'webm').split(';')[0];
      form.append('file', blob, `prompt.${ext}`);
      const qs = new URLSearchParams({
        duration: String(duration),
        waveform: JSON.stringify(peaks || []),
      });
      const r = await api.post(`/voice-prompt?${qs.toString()}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const saved = r.data;
      setPrompt(saved);
      onSaved && onSaved(saved);
      toast.success('Voice prompt saved');
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'Upload failed, please retry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!prompt) return;
    if (!window.confirm('Remove your voice prompt?')) return;
    try {
      await api.delete('/voice-prompt');
      setPrompt(null);
      onSaved && onSaved(null);
      toast.success('Voice prompt removed');
    } catch (err) {
      toast.error('Could not remove voice prompt');
    }
  };

  return (
    <div data-testid="voice-prompt-section">
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-sm text-zinc-600 flex items-center gap-1.5">
          <Mic size={14} strokeWidth={1.8} />
          Voice prompt (optional · 3-30 s)
        </label>
        {prompt && (
          <button
            onClick={handleDelete}
            data-testid="voice-prompt-delete"
            className="text-[11px] text-[#B91C1C] font-semibold flex items-center gap-1 hover:underline"
          >
            <Trash2 size={11} strokeWidth={2} />
            Remove
          </button>
        )}
      </div>
      <p className="text-[11px] text-zinc-500 mb-2 leading-snug">
        Say hi in your own voice — matches hear the real you before you meet.
      </p>

      {saving && (
        <div className="flex items-center gap-2 py-3 px-3 rounded-lg bg-black/[0.04] text-[12px] text-zinc-600">
          <Loader2 size={14} className="animate-spin" /> Uploading & transcribing…
        </div>
      )}

      {!saving && prompt?.url && (
        <div className="mb-2" data-testid="voice-prompt-current">
          <VoiceBubble
            message={{
              voice_url: prompt.url,
              duration: prompt.duration,
              waveform: prompt.waveform,
            }}
            variant="profile"
            isMe
          />
          <p className="text-[10px] text-zinc-500 mt-1.5">
            Tap the record button below to re-record. Uploading a new clip replaces this one.
          </p>
        </div>
      )}

      {!saving && (
        <VoiceRecorder onSend={handleSend} disabled={saving} />
      )}
    </div>
  );
}
